/**
 * This utility builds the regional hierarchy for countries based on the UN M49 standard.
 * It reads from the m49.json file to determine all regional groupings a country belongs to.
 */

import m49Data from '../data/m49.json';

/**
 * Get all regional groupings for a given country M49 code
 * Returns an array of area codes ordered by specificity (closest to furthest)
 * 
 * Priority order:
 * 1. Sub-region (most specific)
 * 2. Intermediate Region (if available)
 * 3. Region (continent)
 * 4. World (001)
 * 
 * @param {string} countryM49Code - The M49 code of the country
 * @returns {string[]} Array of M49 codes [closest region, ..., World]
 */
export function getCountryRegionalHierarchy(countryM49Code) {
    // Find the country entry in m49.json
    const countryEntry = m49Data.find(entry => entry["M49 Code"] === countryM49Code);
    
    if (!countryEntry) {
        console.warn(`Country with M49 code ${countryM49Code} not found in m49.json`);
        // Fallback to just World
        return ["001"];
    }

    const hierarchy = [];
    
    // Add Sub-region if available
    if (countryEntry["Sub-region Code"]) {
        hierarchy.push(countryEntry["Sub-region Code"]);
    }
    
    // Add Intermediate Region if available
    if (countryEntry["Intermediate Region Code"]) {
        hierarchy.push(countryEntry["Intermediate Region Code"]);
    }
    
    // Add Region (continent level)
    if (countryEntry["Region Code"]) {
        hierarchy.push(countryEntry["Region Code"]);
    }
    
    // Always add World
    hierarchy.push("001");
    
    // Remove duplicates while preserving order (in case of data inconsistencies)
    const uniqueHierarchy = [...new Set(hierarchy)];
    
    console.log(`Regional hierarchy for ${countryEntry["Country or Area"]} (${countryM49Code}):`, {
        codes: uniqueHierarchy,
        labels: uniqueHierarchy.map(code => {
            if (code === countryEntry["Sub-region Code"]) return countryEntry["Sub-region Name"];
            if (code === countryEntry["Intermediate Region Code"]) return countryEntry["Intermediate Region Name"];
            if (code === countryEntry["Region Code"]) return countryEntry["Region Name"];
            if (code === "001") return "World";
            return code;
        })
    });
    
    return uniqueHierarchy;
}

/**
 * Get the closest 2 regional groupings plus World
 * This is useful when you want to limit the comparison to the most relevant regions
 * 
 * @param {string} countryM49Code - The M49 code of the country
 * @returns {string[]} Array of M49 codes [2 closest regions, World]
 */
export function getClosestRegions(countryM49Code) {
    const fullHierarchy = getCountryRegionalHierarchy(countryM49Code);
    
    // Always include World, which is the last element
    const world = fullHierarchy[fullHierarchy.length - 1];
    
    // Get up to the first 2 regions (excluding World)
    const closestRegions = fullHierarchy.slice(0, Math.min(2, fullHierarchy.length - 1));
    
    // Combine: closest regions + World
    return [...closestRegions, world];
}

/**
 * Get country name from M49 code
 * 
 * @param {string} countryM49Code - The M49 code of the country
 * @returns {string} Country name or null if not found
 */
export function getCountryName(countryM49Code) {
    const countryEntry = m49Data.find(entry => entry["M49 Code"] === countryM49Code);
    return countryEntry ? countryEntry["Country or Area"] : null;
}

/**
 * Get region name from M49 code
 * 
 * @param {string} regionM49Code - The M49 code of the region
 * @returns {string} Region name or null if not found
 */
export function getRegionName(regionM49Code) {
    if (regionM49Code === "001") return "World";
    
    // Search through all entries to find a match for any regional code
    const entry = m49Data.find(e => 
        e["Region Code"] === regionM49Code ||
        e["Sub-region Code"] === regionM49Code ||
        e["Intermediate Region Code"] === regionM49Code
    );
    
    if (!entry) return null;
    
    if (entry["Region Code"] === regionM49Code) return entry["Region Name"];
    if (entry["Sub-region Code"] === regionM49Code) return entry["Sub-region Name"];
    if (entry["Intermediate Region Code"] === regionM49Code) return entry["Intermediate Region Name"];
    
    return null;
}
